import React, {useCallback, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';

import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {ErrorPage} from 'src/modules/error-page/error-page.components';
import {selectAccountDetails} from 'src/modules/profile/account/services/account-page-slice';
import {UsersGroupsHeader} from 'src/modules/profile/account/tabs/users-and-groups/users-groups-header';
import {getAccountActiveUser} from 'src/openapi/admin/apis/GetAccountActiveUserApi';
import {useAppDispatch} from 'src/store/hooks';

import {Group} from '@pb/kit-component-group';

import {AccountGroupsTable} from './account-groups-table';
import {GroupNameFilter} from './components/group-name-filter';
import {GROUP_TABS} from './constants';
import {CreateGroupDialog} from './create-group';
import {EditGroupDialog} from './edit-group';

import type {GroupSelect} from './typedef';

export const GroupsTab = () => {
    const apiDispatch = useAppDispatch();
    const {accountId} = useParams<{accountId?: string}>();
    const {users} = useSelector(selectAccountDetails);

    const [selectedGroup, setSelectedGroup] = useState<GroupSelect | null>(null);

    const {hasAccess} = useAdminAccessRight();
    const {groupOpen, groupSubmit, groupDelete} = useEventTracking();

    useEffect(() => {
        if (!users && accountId) {
            apiDispatch(getAccountActiveUser({accountId: +accountId}));
        }
    }, [users, accountId, apiDispatch]);

    const openEditMembersDialog = useCallback((groupId: number) => {
        groupOpen({viewType: 'Existing Group'});
        setSelectedGroup({groupId, view: GROUP_TABS.MEMBERS.name});
    }, [groupOpen]);

    const openEditGroupDialog = useCallback((groupId: number) => {
        groupOpen({viewType: 'Existing Group'});
        setSelectedGroup({groupId, view: GROUP_TABS.SETTINGS.name});
    }, [groupOpen]);

    const closeEditGroupDialog = useCallback((submit?: boolean) => {
        if (submit !== undefined) {
            groupSubmit({viewType: 'Existing Group', submit});
        }
        setSelectedGroup(null);
    }, [groupSubmit]);

    if (!hasAccess(ACCESS_RIGHTS.GROUP_SCREEN_VIEW)) {
        return <ErrorPage handler="/" title="Access Denied" subtitle="You have no access to view Groups"/>;
    }

    return (
        <>
            <UsersGroupsHeader activeTab="groups">
                <Group justify="space-between">
                    <GroupNameFilter/>
                    {hasAccess(ACCESS_RIGHTS.GROUP_SCREEN_CREATE) && <CreateGroupDialog/>}
                </Group>
            </UsersGroupsHeader>
            <AccountGroupsTable
                onSelectGroupMembers={openEditMembersDialog}
                onSelectGroup={openEditGroupDialog}
            />
            <EditGroupDialog
                selectedGroup={selectedGroup}
                onClose={closeEditGroupDialog}
                onDelete={groupDelete}
            />
        </>
    );
};
